import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { ConfirmationDialogProps } from "../Dialogs/ConfirmationDialog";
import { DefaultUnsavedChangesConfirmationDialogProps } from "./constants";

type ConfirmationConfig = {
  dialogProps?: Partial<
    Omit<ConfirmationDialogProps, "isOpen" | "onConfirm" | "onCancel">
  >;
};

type BlockerContextType = {
  isDisabled: boolean;
  disable: (isDisabled: boolean) => Promise<void>;
  confirm: (config?: ConfirmationConfig) => Promise<boolean>;
  confirmationConfig?: ConfirmationConfig;
  includeQueryParams?: boolean;
  hasChanges: boolean;
  reportHasChanges: (blockerId: string, hasChanges: boolean) => void;
};

export const BlockerContext = createContext<BlockerContextType | undefined>(
  undefined
);

type Props = {
  children: ReactNode | ((context: BlockerContextType) => ReactNode);
  confirmationConfig?: ConfirmationConfig;
  includeQueryParams?: boolean;
};
export const NavigationBlockerProvider = ({
  children,
  confirmationConfig,
  includeQueryParams,
}: Props) => {
  const [isDisabled, setIsDisabled] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const dirtyBlockerIds = useRef(new Set<string>());

  const confirmationDialog = useConfirmationDialog({
    ...DefaultUnsavedChangesConfirmationDialogProps,
    ...confirmationConfig?.dialogProps,
  });

  const disable = useCallback(async (value: boolean): Promise<void> => {
    return new Promise((success) => {
      setIsDisabled(value);
      setTimeout(() => {
        return success();
      }, 0);
    });
  }, []);

  const reportHasChanges = useCallback(
    (blockerId: string, blockerHasChanges: boolean) => {
      if (blockerHasChanges) {
        dirtyBlockerIds.current.add(blockerId);
      } else {
        dirtyBlockerIds.current.delete(blockerId);
      }
      setHasChanges(dirtyBlockerIds.current.size > 0);
    },
    []
  );

  const confirm = useCallback(
    async (config?: ConfirmationConfig): Promise<boolean> => {
      return confirmationDialog.confirm({ ...config?.dialogProps });
    },
    [confirmationDialog]
  );

  const context = {
    isDisabled,
    disable,
    confirm,
    confirmationConfig,
    includeQueryParams,
    hasChanges,
    reportHasChanges,
  };
  return (
    <BlockerContext.Provider value={context}>
      {typeof children === "function" ? children(context) : children}
      {confirmationDialog.confirmationDialog}
    </BlockerContext.Provider>
  );
};

export const useNavigationBlockerContext = () => {
  const context = useContext(BlockerContext);
  if (!context) {
    throw new Error(
      "useNavigationBlockerContext must be used within a NavigationBlockerProvider"
    );
  }
  return context;
};
